import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import Swal from "sweetalert2";

import LocationBenefits from './components/LocationBenefits';
import AreaDetails from './components/AreaDetails';
import PropertyBasicDetails from './components/PropertyBasicDetails';
import ContactDetails from './components/ContactDetails';
import PriceDetails from './components/PriceDetails';
import LocationDetails from './components/LocationDetails';
import MediaSection from './components/MediaSection';
import PropertyFeatures from './components/PropertyFeatures';
import FeaturesAndAmenities from './components/FeaturesAndAmenities';
import SectionDivider from './components/SectionDivider';

import {
    getPropertyFiltersAction,
    createPropertyAction,
    getPropertyAction,
    updatePropertyAction
} from '../../../store/actions/property/PropertyActions';
import { getCustomersAction } from '../../../store/actions/customer/CustomerActions';

import { usePropertyForm } from './hooks/usePropertyForm';

const AddProperty = () => {
    const autocompleteRef = useRef(null);
    const prevEditModeRef = useRef(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { property, filters } = useSelector(state => state.property);

    const {
        formData,
        setFormData,
        errors,
        // eslint-disable-next-line no-unused-vars
        setErrors,
        handleInputChange,
        updateContactDetails,
        handleAmenityToggle,
        handleFeatureToggle,
        validateForm,
        clearError,
        resetForm
    } = usePropertyForm();

    const [media, setMedia] = useState([]);
    const [floorPlanUrls, setFloorPlanUrls] = useState([]);
    const [areaDetails, setAreaDetails] = useState([
        { name: 'Area Size', value: '', unit: '', label: 'area' },
        { name: 'Carpet Area', value: '', unit: '', label: 'carpetArea' },
        { name: 'Build-up Area', value: '', unit: '', label: 'buildUpArea' },
        { name: 'Super Build-up Area', value: '', unit: '', label: 'superBuildUpArea' },
    ]);
    const [benefits, setBenefits] = useState([{ name: 'Hospital', value: '5', unit: 'Km' }]);

    const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
    const [propertyTypeFields, setPropertyTypeFields] = useState([]);
    const [propertyTypeSelectedFields, setPropertyTypeSelectedFields] = useState([]);
    const [listingTypeOptions, setListingTypeOptions] = useState([]);
    const [listingTypeFields, setListingTypeFields] = useState([]);
    const [listingTypeSelectedFields, setListingTypeSelectedFields] = useState([]);
    const [furnishingStatusOptions, setFurnishingStatusOptions] = useState([]);
    const [ownershipTypesOptions, setOwnershipTypesOptions] = useState([]);
    const [availabilityStatusOptions, setAvailabilityStatusOptions] = useState([]);
    const [facingDirectionsOptions, setFacingDirectionsOptions] = useState([]);
    const [features, setFeatures] = useState([]);
    const [amenities, setAmenities] = useState([]);
    const [areaUnits, setAreaUnits] = useState([]);

    const resetAllFormData = useCallback(() => {
        resetForm();
        setMedia([]);
        setFloorPlanUrls([]);
        setAreaDetails([
            { name: 'Area Size', value: '', unit: '', label: 'area' },
            { name: 'Carpet Area', value: '', unit: '', label: 'carpetArea' },
            { name: 'Build-up Area', value: '', unit: '', label: 'buildUpArea' },
            { name: 'Super Build-up Area', value: '', unit: '', label: 'superBuildUpArea' },
        ]);
        setBenefits([{ name: 'Hospital', value: '5', unit: 'Km' }]);
        setPropertyTypeSelectedFields([]);
        setListingTypeSelectedFields([]);
    }, [resetForm]);

    const updatePropertyTypeFields = (id) => {
        const selected = propertyTypeFields.find(item => item._id === id);
        if (selected) setPropertyTypeSelectedFields(selected.fields);
    };

    const updateListingTypeFields = (id) => {
        const selected = listingTypeFields.find(item => item._id === id);
        if (selected) setListingTypeSelectedFields(selected.fields);
    };

    const handlePlaceChanged = () => {
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.address_components) return;

        const components = place.address_components;
        const subAddressParts = components
            .filter(comp =>
                comp.types.includes('sublocality_level_2') ||
                comp.types.includes('sublocality_level_1') ||
                comp.types.includes('locality')
            )
            .slice(0, 3)
            .map(comp => comp.long_name);

        const compactAddress = subAddressParts.join(', ');

        const updated = {
            address: compactAddress,
            pincode: '',
            city: '',
            state: '',
            landmark: place.name || '',
            latitude: '',
            longitude: ''
        };

        for (const component of components) {
            const types = component.types;
            if (types.includes('postal_code')) updated.pincode = component.long_name;
            if (types.includes('locality')) updated.city = component.long_name;
            if (types.includes('administrative_area_level_1')) updated.state = component.long_name;
        }

        
        if (place.geometry?.location) {
            updated.latitude = place.geometry.location.lat();
            updated.longitude = place.geometry.location.lng();
        }

        setFormData(prev => ({ ...prev, ...updated }));
    };

    const handleSubmit = async () => {
        if (!validateForm(media, areaDetails, propertyTypeSelectedFields)) return;

        const payload = {
            ...formData,
            imageUrls: media,
            floorPlanUrl: floorPlanUrls,
            nearbyLandmarks: benefits,
            areaSize: parseFloat(areaDetails[0].value || 0),
            carpetArea: parseFloat(areaDetails[1].value || 0),
            builtUpArea: parseFloat(areaDetails[2].value || 0),
            superBuiltUpArea: parseFloat(areaDetails[3].value || 0),
            areaUnitId: areaDetails[0].unit || areaUnits[0]?._id,
            carpetAreaUnitId: areaDetails[1].unit || areaUnits[1]?._id,
            builtUpAreaUnitId: areaDetails[2].unit || areaUnits[2]?._id,
            superBuiltAreaUnitId: areaDetails[3].unit || areaUnits[3]?._id,
        };

        try {
            let response;
            if (isEditMode) {
                response = await dispatch(updatePropertyAction(id, payload));
                if (response.data?.success) {
                    Swal.fire({ icon: 'success', title: 'Success!', text: 'Property updated' });
                } else {
                    Swal.fire({ icon: 'error', title: 'Error', text: response.data?.msg || 'Something went wrong' });
                }
            } else {
                response = await dispatch(createPropertyAction(payload));
                if (response.data?.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: 'Property added successfully'
                    }).then(() => {
                        resetAllFormData();
                        navigate('/property-list');
                    });
                } else {
                    Swal.fire({ icon: 'error', title: 'Error', text: response.data?.msg || 'Something went wrong' });
                }
            }
        } catch (err) {
            console.error('Error submitting property:', err);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong' });
        }
    };

    // Track previous edit mode to detect mode changes    
    useEffect(() => {
        // Only reset when switching from edit mode to add mode
        if (prevEditModeRef.current && !isEditMode) {
            resetForm();
            setMedia([]);
            setFloorPlanUrls([]);
            setAreaDetails([
                { name: 'Area Size', value: '', unit: '', label: 'area' },
                { name: 'Carpet Area', value: '', unit: '', label: 'carpetArea' },
                { name: 'Build-up Area', value: '', unit: '', label: 'buildUpArea' },
                { name: 'Super Build-up Area', value: '', unit: '', label: 'superBuildUpArea' },
            ]);
            setBenefits([{ name: 'Hospital', value: '5', unit: 'Km' }]);
            setPropertyTypeSelectedFields([]);
            setListingTypeSelectedFields([]);
        }
        
        // Update the ref for next comparison
        prevEditModeRef.current = isEditMode;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode]); // Only depend on isEditMode - resetForm excluded intentionally

    useEffect(() => {
        dispatch(getPropertyFiltersAction());
        dispatch(getCustomersAction());
    }, [dispatch]);

    useEffect(() => {
        if (filters && Object.keys(filters).length > 0) {
            setPropertyTypeOptions(filters.propertyTypes.map(p => ({ value: p._id, label: p.name })));
            setListingTypeOptions(filters.listingTypes.map(p => ({ value: p._id, label: p.name })));
            setFurnishingStatusOptions(filters.furnishingStatuses.map(p => ({ value: p._id, label: p.name })));
            setOwnershipTypesOptions(filters.ownershipTypes.map(p => ({ value: p._id, label: p.name })));
            setAvailabilityStatusOptions(filters.availabilityStatuses.map(p => ({ value: p._id, label: p.name })));
            setFacingDirectionsOptions(filters.facingDirections.map(p => ({ value: p._id, label: p.name })));
            setAreaUnits(filters.areaUnits);
            setAmenities(filters.amenities);
            setFeatures(filters.features);

            setPropertyTypeFields(filters.propertyTypes.map(t => ({ _id: t._id, name: t.name, fields: t.fields })));
            setListingTypeFields(filters.listingTypes.map(t => ({ _id: t._id, name: t.name, fields: t.fields })));
        }
    }, [filters]);

    useEffect(() => {
        if (isEditMode) {
            dispatch(getPropertyAction(id,'propertyTypeId,listingTypeId,amenityIds'));
        }
    }, [isEditMode, id, dispatch]);

    useEffect(() => {
        if (isEditMode && property?._id) {
            const p = property;
            setFormData(prev => ({
                ...prev,
                customerId: p.customerId || null,
                title: p.title || '',
                description: p.description || '',
                propertyTypeId: p.propertyTypeId?._id || null,
                listingTypeId: p.listingTypeId?._id || null,
                furnishingStatusId: p.furnishingStatusId || null,
                ownershipTypeId: p.ownershipTypeId || null,
                availabilityStatusId: p.availabilityStatusId || null,
                propertyAge: p.propertyAge || '',
                price: p.price || '',
                isNegotiable: p.isNegotiable || false,
                loanEligible: p.loanEligible || false,
                bachelorsAllowed: p.bachelorsAllowed || false,
                reraApproved: p.reraApproved || false,
                reraId: p.reraId || '',
                address: p.address || '',
                city: p.city || '',
                state: p.state || '',
                pincode: p.pincode || '',
                landmark: p.landmark || '',
                latitude: p.latitude || '',
                longitude: p.longitude || '',
                carParking: p.carParking || '',
                numberOfBedrooms: p.numberOfBedrooms || '',
                numberOfBathrooms: p.numberOfBathrooms || '',
                numberOfBalconies: p.numberOfBalconies || '',
                numberOfKitchens: p.numberOfKitchens || '',
                locationMapUrl: p.locationMapUrl || '',
                videoUrl: p.videoUrl || '',
                facingDirectionId: p.facingDirectionId || null,
                imageUrls: p.imageUrls || [],
                floorPlanUrl: p.floorPlanUrl || [],
                amenityIds: (p.amenityIds || []).map(a => typeof a === 'string' ? a : a._id),
                featureIds: p.featureIds || [],
                email: p.email || '',
                whatsappNumber: p.whatsappNumber || '',
                brokerageApplicable: p.brokerageApplicable || false,
                brokerageAmount: p.brokerageAmount ?? '',
                chatEnabled: p.chatEnabled ?? true,
                isVerifiedContact: p.isVerifiedContact ?? true,
                viewsCount: p.viewsCount || 0,
                likesCount: p.likesCount || 0
            }));

            setAreaDetails(prev => prev.map(item => {
                let value = '';
                let unit = '';
                
                switch(item.label) {
                    case 'area':
                        value = p.areaSize?.toString() || '';
                        unit = p.areaUnitId || '';
                        break;
                    case 'carpetArea':
                        value = p.carpetArea?.toString() || '';
                        unit = p.carpetAreaUnitId || '';
                        break;
                    case 'buildUpArea':
                        value = p.builtUpArea?.toString() || '';
                        unit = p.builtUpAreaUnitId || '';
                        break;
                    case 'superBuildUpArea':
                        value = p.superBuiltUpArea?.toString() || '';
                        unit = p.superBuiltAreaUnitId || '';
                        break;
                    default:
                        value = '';
                        unit = '';
                }
                
                return {
                    ...item,
                    value,
                    unit
                };
            }));

            setBenefits(p.nearbyLandmarks || []);
            setPropertyTypeSelectedFields(p.propertyTypeId?.fields || []);
            setListingTypeSelectedFields(p.listingTypeId?.fields || []);
            setMedia(p.imageUrls || []);
            setFloorPlanUrls(p.floorPlanUrl || []);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, property]);

    return (
        <div className="container">
            <div className="row">
            <div className="col-12">
                <div className="card">
                <div className="card-header">
                    <h4 className="card-title">Add Property</h4>
                </div>
                <div className="card-body">
                    <form>
                        <SectionDivider title="Basic Details" />
                        <div className="row">
                            <PropertyBasicDetails
                                formData={formData}
                                errors={errors}
                                propertyTypeOptions={propertyTypeOptions}
                                listingTypeOptions={listingTypeOptions}
                                furnishingStatusOptions={furnishingStatusOptions}
                                ownershipTypesOptions={ownershipTypesOptions}
                                availabilityStatusOptions={availabilityStatusOptions}
                                facingDirectionsOptions={facingDirectionsOptions}
                                propertyTypeSelectedFields={propertyTypeSelectedFields}
                                handleInputChange={handleInputChange}
                                updatePropertyTypeFields={updatePropertyTypeFields}
                                updateListingTypeFields={updateListingTypeFields}
                            />
                        </div>

                        <SectionDivider title="App Customer Select" />
                        <div className="row">
                            <ContactDetails
                                formData={formData}
                                errors={errors}
                                handleInputChange={handleInputChange}
                                updateContactDetails={updateContactDetails}
                            />
                        </div>

                        <SectionDivider title="Price Details" />
                        <div className="row">
                            <PriceDetails
                                formData={formData}
                                errors={errors}
                                listingTypeSelectedFields={listingTypeSelectedFields}
                                propertyTypeSelectedFields={propertyTypeSelectedFields}
                                handleInputChange={handleInputChange}
                            />
                        </div>

                        <SectionDivider title="Location Information" />
                        <div className="row">
                            <LocationDetails
                                autocompleteRef={autocompleteRef}
                                formData={formData}
                                handlePlaceChanged={handlePlaceChanged}
                                handleInputChange={handleInputChange}
                                errors={errors}
                            />
                        </div>

                        <SectionDivider title="Location Benefits" />        
                        <div className="row">
                            <LocationBenefits
                                benefits={benefits}
                                setBenefits={setBenefits}
                            />
                        </div>

                        {areaDetails.some(detail => propertyTypeSelectedFields.includes(detail.label)) && (
                            <>
                                <SectionDivider title="Area Details" />        
                                <div className="row">
                                    <AreaDetails
                                        areaUnits={areaUnits}
                                        areaDetails={areaDetails}
                                        setAreaDetails={setAreaDetails}
                                        propertyTypeSelectedFields={propertyTypeSelectedFields}
                                        errors={errors}
                                        clearError={clearError}
                                    />
                                </div>
                            </>
                        )}

                        {propertyTypeSelectedFields.includes("propertyFeatures") && (
                            <>
                                <SectionDivider title="Property Features" />
                                <div className="row">
                                    <PropertyFeatures
                                        formData={formData}
                                        setFormData={setFormData}
                                    />
                                </div>
                            </>
                        )}

                        <SectionDivider title="Amenities & Features" />
                        <div className="row">
                            <FeaturesAndAmenities
                                formData={formData}
                                amenities={amenities}
                                features={features}
                                handleAmenityToggle={handleAmenityToggle}
                                handleFeatureToggle={handleFeatureToggle}
                            />
                        </div>

                        <SectionDivider title="Media Upload" />
                        <div className="row">
                            <MediaSection
                                media={media}
                                setMedia={setMedia}
                                formData={formData}
                                handleInputChange={handleInputChange}
                                floorPlanUrls={floorPlanUrls}
                                setFloorPlanUrls={setFloorPlanUrls}
                                propertyTypeSelectedFields={propertyTypeSelectedFields}
                            />
                        </div>

                        <div className="row mt-4">
                            <div className="col-sm-12">
                            <button
                                onClick={handleSubmit}
                                type="button"
                                className="btn btn-sm btn-primary me-2"
                            >
                                Submit
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger light"
                            >
                                Cancel
                            </button>
                            </div>

                            {Object.keys(errors).length > 0 && (
                            <div className="text-danger mt-3">
                                Fill all the required fields
                            </div>
                            )}
                        </div>
                    </form>
                </div>
                </div>
            </div>
            </div>
        </div>
    );

};

export default AddProperty;
